import { onBeforeUnmount, onMounted, watch, type Ref } from "vue";

// Полноэкранные оверлеи/модалки переключаются через v-if — без записи в
// историю и смены роута, поэтому кнопка "назад" их не закрывала, а
// навигировала/листала страницу под ними. Решение: пушить запись истории,
// когда оверлей открыт, и снимать её при закрытии, чтобы "назад" закрывал
// оверлей (а для многошаговых — сначала шёл на предыдущий шаг).
//
// Общий стек (не состояние на инстанс) гарантирует, что одно нажатие "назад"
// закрывает только верхний оверлей — вложенность разворачивается по слою.
const stack: Array<() => void> = [];
let listenerAttached = false;
let suppressPopstateCount = 0;

function attachListener() {
  if (listenerAttached || typeof window === "undefined") return;
  listenerAttached = true;
  window.addEventListener("popstate", () => {
    if (suppressPopstateCount > 0) {
      suppressPopstateCount--;
      return;
    }
    const top = stack[stack.length - 1];
    if (top) top();
  });
}

// onBack (опционально): для многошаговых модалок (регистрация на маршрут).
// Возвращает true, если "назад" отработан переходом на предыдущий шаг —
// тогда модалка остаётся открытой, а запись истории переставляется заново,
// чтобы следующий "назад" снова был перехвачен. false/undefined — закрыть.
function makeHandler(close: () => void, onBack: (() => boolean) | undefined, setFlag: (v: boolean) => void) {
  return () => {
    if (onBack && onBack()) {
      history.pushState({ ecowayOverlay: true }, "");
      return;
    }
    setFlag(true);
    close();
  };
}

// Вызывать один раз в компоненте, монтируемом/размонтируемом через v-if,
// передав тот же обработчик закрытия, что и у кнопки закрытия.
export function useHistoryBackClose(close: () => void, onBack?: () => boolean) {
  if (typeof window === "undefined") return;
  attachListener();
  let closingFromPopstate = false;
  const handler = makeHandler(close, onBack, (v) => {
    closingFromPopstate = v;
  });

  onMounted(() => {
    history.pushState({ ecowayOverlay: true }, "");
    stack.push(handler);
  });

  onBeforeUnmount(() => {
    const idx = stack.indexOf(handler);
    if (idx !== -1) stack.splice(idx, 1);
    // Запись истории убирается, только если размонтирование НЕ вызвано popstate
    // (браузер уже сделал переход сам) — иначе закрытие через кнопку в приложении
    // оставит лишний шаг в истории. suppressPopstateCount не даёт этому
    // самоинициированному popstate ошибочно закрыть следующий оверлей в стеке.
    if (!closingFromPopstate) {
      suppressPopstateCount++;
      history.back();
    }
  });
}

// Для модалок, живущих инлайновым v-if внутри постоянно смонтированного
// компонента (напр. видео-модалка камеры в NodesSection) — там нет своего
// mount/unmount, так что следим за реактивным флагом открытия.
export function useHistoryBackCloseWhen(isOpen: Ref<boolean>, close: () => void) {
  if (typeof window === "undefined") return;
  attachListener();
  let closingFromPopstate = false;
  const handler = makeHandler(close, undefined, (v) => {
    closingFromPopstate = v;
  });

  watch(isOpen, (open, wasOpen) => {
    if (open && !wasOpen) {
      closingFromPopstate = false;
      history.pushState({ ecowayOverlay: true }, "");
      stack.push(handler);
    } else if (!open && wasOpen) {
      const idx = stack.indexOf(handler);
      if (idx !== -1) stack.splice(idx, 1);
      if (!closingFromPopstate) {
        suppressPopstateCount++;
        history.back();
      }
    }
  });

  onBeforeUnmount(() => {
    const idx = stack.indexOf(handler);
    if (idx !== -1) {
      stack.splice(idx, 1);
      if (!closingFromPopstate) {
        suppressPopstateCount++;
        history.back();
      }
    }
  });
}
